import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
} from "react-hook-form";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Button, LoadingState } from "~/common/components";
import Input from "~/common/components/ui/Input";
import TextArea from "~/common/components/ui/Textarea";
import { BUTTON_VARIANTS, TEXT_NOTE_LENGTH } from "~/common/constants";
import { scrollToFormError } from "~/common/utils";
import {
  ConsultationInputLayout,
  MentalStatusExamination,
} from "~/providers/components";
import { soapNoteGenerationStatusMessages } from "~/providers/constants/soapNoteGenerationStatus";
import {
  useGetPatientZoomMeetingSoapNote,
  useUpsertPatientZoomMeetingSoapNote,
} from "~/providers/hooks";
import {
  ConsultationCompletionDataInputType,
  isSoapNoteGenerationFailed,
  isSoapNoteGenerationPending,
  SessionSoapNoteFormInput,
  sessionSoapNoteFormSchema,
  SessionSoapNoteFormValues,
} from "~/providers/utils";
import { SessionSoapNoteConfirmationModal } from "./SessionSoapNoteConfirmationModal";

const getDefaultValues = (): SessionSoapNoteFormInput => ({
  completeAndSign: false,
  data: {
    subjective: "",
    diagnostic_considerations: "",
    diagnosis_codes: "",
    treatment_plan: "",
    cpt_codes: "",
    appearance: { values: [] },
    behavior: { values: [] },
    speech: { values: [] },
    mood: { values: [] },
    affect: { values: [] },
    thought_process: { values: [] },
    thought_content: { values: [] },
    attention_and_concentration: { values: [] },
    judgment_and_insight: { values: [] },
  },
});

const parseCodesInput = (value: string | undefined): string[] =>
  (value ?? "")
    .split(/[,\n]/)
    .map((code) => code.trim())
    .filter(Boolean);

const sanitizeCompletedAnswer = (
  value: SessionSoapNoteFormInput["data"]["appearance"] | undefined,
) => ({
  values:
    value?.values?.map((item) => ({
      value: item.value,
      extra_data: item.extra_data ?? undefined,
    })) ?? [],
  extra_data: value?.extra_data ?? undefined,
});

export const SessionSoapNote = () => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const toggleConfirmationModal = () =>
    setShowConfirmationModal((prev) => !prev);

  const { patientId, sessionId } = useParams();
  const hasRouteParams = Boolean(patientId && sessionId);

  const { data: soapNote, isLoading: isLoadingSoapNote } =
    useGetPatientZoomMeetingSoapNote({
      patientId: patientId ?? "",
      sessionId: sessionId ?? "",
      enabled: hasRouteParams,
    });

  const { mutate: upsertSoapNote, isPending } =
    useUpsertPatientZoomMeetingSoapNote(patientId ?? "", sessionId ?? "");
  const isSigned = Boolean(soapNote?.completed_at);

  const defaultValues = useMemo(getDefaultValues, []);

  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<SessionSoapNoteFormInput>({
    resolver: zodResolver(sessionSoapNoteFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!soapNote) return;

    reset({
      completeAndSign: false,
      data: {
        subjective: soapNote.subjective ?? "",
        diagnostic_considerations: soapNote.diagnostic_considerations ?? "",
        diagnosis_codes: soapNote.diagnosis_codes?.join(", ") ?? "",
        treatment_plan: soapNote.treatment_plan ?? "",
        cpt_codes: soapNote.cpt_codes?.join(", ") ?? "",
        appearance: soapNote.appearance ?? { values: [] },
        behavior: soapNote.behavior ?? { values: [] },
        speech: soapNote.speech ?? { values: [] },
        mood: soapNote.mood ?? { values: [] },
        affect: soapNote.affect ?? { values: [] },
        thought_process: soapNote.thought_process ?? { values: [] },
        thought_content: soapNote.thought_content ?? { values: [] },
        attention_and_concentration: soapNote.attention_and_concentration ?? {
          values: [],
        },
        judgment_and_insight: soapNote.judgment_and_insight ?? { values: [] },
      },
    });
  }, [reset, soapNote]);

  if (!hasRouteParams) return null;

  if (isLoadingSoapNote) {
    return (
      <div className="flex h-full items-center justify-center py-8">
        <LoadingState />
      </div>
    );
  }

  if (!soapNote) {
    return (
      <div className="py-8 text-sm text-gray-500">
        SOAP note is not yet available.
      </div>
    );
  }

  if (isSoapNoteGenerationPending(soapNote)) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8">
        <LoadingState className="h-auto" />
        <p className="text-sm text-gray-500">
          {soapNoteGenerationStatusMessages.IN_PROGRESS}
        </p>
      </div>
    );
  }

  const generationFailed = isSoapNoteGenerationFailed(soapNote);

  const mseGetValues =
    getValues as unknown as UseFormGetValues<ConsultationCompletionDataInputType>;
  const mseRegister =
    register as unknown as UseFormRegister<ConsultationCompletionDataInputType>;
  const mseErrors =
    errors as unknown as FieldErrors<ConsultationCompletionDataInputType>;
  const mseControl =
    control as unknown as Control<ConsultationCompletionDataInputType>;

  const submitSoapNote = (
    values: SessionSoapNoteFormValues,
    options?: { onSuccess?: () => void },
  ) => {
    upsertSoapNote(
      {
        data: {
          complete_and_sign: values.completeAndSign,
          subjective: values.data.subjective,
          diagnostic_considerations: values.data.diagnostic_considerations,
          diagnosis_codes: parseCodesInput(values.data.diagnosis_codes),
          treatment_plan: values.data.treatment_plan,
          cpt_codes: parseCodesInput(values.data.cpt_codes),
          appearance: sanitizeCompletedAnswer(values.data.appearance),
          behavior: sanitizeCompletedAnswer(values.data.behavior),
          speech: sanitizeCompletedAnswer(values.data.speech),
          mood: sanitizeCompletedAnswer(values.data.mood),
          affect: sanitizeCompletedAnswer(values.data.affect),
          thought_process: sanitizeCompletedAnswer(values.data.thought_process),
          thought_content: sanitizeCompletedAnswer(values.data.thought_content),
          attention_and_concentration: sanitizeCompletedAnswer(
            values.data.attention_and_concentration,
          ),
          judgment_and_insight: sanitizeCompletedAnswer(
            values.data.judgment_and_insight,
          ),
        },
      },
      options,
    );
  };

  const onSubmit = (values: SessionSoapNoteFormValues) => {
    if (values.completeAndSign) {
      toggleConfirmationModal();
      return;
    }

    submitSoapNote(values);
  };

  const handleCompleteSoapNote = () => {
    submitSoapNote(
      { ...getValues(), completeAndSign: true } as SessionSoapNoteFormValues,
      { onSuccess: toggleConfirmationModal },
    );
  };

  const handleCompleteAndSignChange = (completeAndSign: boolean) => {
    setValue("completeAndSign", completeAndSign);
  };

  return (
    <form
      className="flex size-full flex-col gap-6"
      onSubmit={handleSubmit(onSubmit, scrollToFormError)}
    >
      {generationFailed ? (
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">
          {soapNoteGenerationStatusMessages.FAILED}
        </div>
      ) : null}

      <div className="flex flex-col gap-4">
        <ConsultationInputLayout label="Subjective">
          <TextArea
            placeholder="Add subjective notes"
            error={errors.data?.subjective?.message}
            style={{ fieldSizing: "content", minHeight: "82px" }}
            {...register("data.subjective")}
            maxCharacters={TEXT_NOTE_LENGTH}
            charactersCount={watch("data.subjective")?.length}
            disabled={isPending || isSigned}
          />
        </ConsultationInputLayout>

        <ConsultationInputLayout label="Objective (Mental status examination)">
          <MentalStatusExamination
            getValues={mseGetValues}
            register={mseRegister}
            errors={mseErrors}
            control={mseControl}
            isDisabled={isPending || isSigned}
          />
        </ConsultationInputLayout>

        <ConsultationInputLayout label="Assessment">
          <div className="flex flex-col gap-4">
            <TextArea
              placeholder="Add your observations and diagnostic considerations"
              error={errors.data?.diagnostic_considerations?.message}
              style={{ fieldSizing: "content", minHeight: "82px" }}
              {...register("data.diagnostic_considerations")}
              maxCharacters={TEXT_NOTE_LENGTH}
              charactersCount={watch("data.diagnostic_considerations")?.length}
              disabled={isPending || isSigned}
            />
            <Input
              placeholder="Add diagnosis codes separated by commas"
              error={errors.data?.diagnosis_codes?.message}
              {...register("data.diagnosis_codes")}
              maxLength={5000}
              charactersCount={watch("data.diagnosis_codes")?.length}
              disabled={isPending || isSigned}
            />
          </div>
        </ConsultationInputLayout>

        <ConsultationInputLayout label="Plan">
          <div className="flex flex-col gap-4">
            <TextArea
              placeholder="Add your treatment plan"
              error={errors.data?.treatment_plan?.message}
              style={{ fieldSizing: "content", minHeight: "82px" }}
              {...register("data.treatment_plan")}
              maxCharacters={TEXT_NOTE_LENGTH}
              charactersCount={watch("data.treatment_plan")?.length}
              disabled={isPending || isSigned}
            />
            <Input
              placeholder="Add CPT codes separated by commas"
              error={errors.data?.cpt_codes?.message}
              {...register("data.cpt_codes")}
              maxLength={5000}
              charactersCount={watch("data.cpt_codes")?.length}
              disabled={isPending || isSigned}
            />
          </div>
        </ConsultationInputLayout>
      </div>

      <div className="flex w-fit gap-2 self-end pb-3">
        <Button
          className="w-fit"
          type="submit"
          size="sm"
          variant={BUTTON_VARIANTS.outline_black}
          isLoading={isPending}
          disabled={!isDirty || isPending || isSigned}
          onClick={() => handleCompleteAndSignChange(false)}
        >
          Save changes
        </Button>
        <Button
          className="w-fit"
          type="submit"
          size="sm"
          disabled={isPending || isSigned}
          onClick={() => handleCompleteAndSignChange(true)}
        >
          Complete SOAP note
        </Button>
      </div>
      <SessionSoapNoteConfirmationModal
        show={showConfirmationModal}
        onClose={toggleConfirmationModal}
        isPending={isPending}
        onSubmit={handleCompleteSoapNote}
      />
    </form>
  );
};
